import { useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  UsersRound,
  Building2,
  Users,
  Loader2,
  Edit,
  Trash2,
  Zap,
  CheckCircle,
  AlertCircle,
  Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@flote/ui';
import { deliveryTsr } from '../../api';
import { TeamFormModal } from '../../components/teams/TeamFormModal';
import { getErrorMessage } from '../../lib/error-utils';
import type { UpdateTeamRequest, Driver } from '@flote/contracts/http';

export function TeamDetailPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDispatching, setIsDispatching] = useState(false);

  // Fetch team details
  const { data: teamResponse, isLoading: teamLoading, refetch: refetchTeam } = deliveryTsr.adminTeams.getById.useQuery({
    queryKey: ['team', teamId],
    queryData: { params: { teamId: teamId! } },
  });
  const team = teamResponse?.status === 200 ? teamResponse.body : null;

  // Fetch hubs for reference and edit form
  const { data: hubsResponse } = deliveryTsr.adminHubs.list.useQuery({
    queryKey: ['hubs-list'],
  });
  const hubs = hubsResponse?.status === 200 ? hubsResponse.body : [];
  const hub = team?.hub ? hubs.find(h => h.id === team.hub) : null;

  // Fetch drivers for worker/manager display (used for edit form)
  const { data: driversResponse } = deliveryTsr.adminDrivers.list.useQuery({
    queryKey: ['drivers-list'],
    queryData: { query: { limit: 100 } },
  });
  const drivers = driversResponse?.status === 200 ? driversResponse.body : [];
  const driversMap = new Map(drivers.map(d => [d.id, d]));

  // Fetch team members from junction tables
  const { data: membersResponse } = deliveryTsr.adminTeams.getMembers.useQuery({
    queryKey: ['team-members', teamId],
    queryData: { params: { teamId: teamId! } },
    enabled: !!teamId,
  });
  const teamMembers = membersResponse?.status === 200 ? membersResponse.body : null;

  // Mutations
  const updateTeamMutation = deliveryTsr.adminTeams.update.useMutation();
  const deleteTeamMutation = deliveryTsr.adminTeams.delete.useMutation();
  const autoDispatchMutation = deliveryTsr.adminTeams.autoDispatch.useMutation();

  const handleUpdateTeam = useCallback(async (data: UpdateTeamRequest) => {
    if (!teamId) return;

    setIsUpdating(true);
    try {
      const response = await updateTeamMutation.mutateAsync({
        params: { teamId },
        body: data,
      });

      if (response.status === 200) {
        toast.success('Team updated successfully');
        setIsEditModalOpen(false);
        refetchTeam();
      } else {
        const errorBody = response.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to update team');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update team'));
      console.error('Update team error:', error);
    } finally {
      setIsUpdating(false);
    }
  }, [teamId, updateTeamMutation, refetchTeam]);

  const handleDeleteTeam = useCallback(async () => {
    if (!teamId) return;

    setIsDeleting(true);
    try {
      const response = await deleteTeamMutation.mutateAsync({
        params: { teamId },
        body: {},
      });

      if (response.status === 204) {
        toast.success('Team deleted successfully');
        navigate('/teams');
      } else {
        const errorBody = response.body as unknown as { message?: string };
        toast.error(errorBody?.message || 'Failed to delete team');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete team'));
      console.error('Delete team error:', error);
    } finally {
      setIsDeleting(false);
    }
  }, [teamId, deleteTeamMutation, navigate]);

  const handleAutoDispatch = useCallback(async () => {
    if (!teamId) return;

    setIsDispatching(true);
    try {
      const response = await autoDispatchMutation.mutateAsync({
        params: { teamId },
        body: {},
      });

      if (response.status === 200) {
        toast.success('Auto-dispatch triggered successfully', {
          description: `Dispatch ID: ${response.body.dispatchId}`,
        });
      } else {
        const errorBody = response.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to trigger auto-dispatch');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to trigger auto-dispatch'));
      console.error('Auto-dispatch error:', error);
    } finally {
      setIsDispatching(false);
    }
  }, [teamId, autoDispatchMutation]);

  if (teamLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!team) {
    return (
      <div className="space-y-6">
        <Link to="/teams" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Teams
        </Link>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <UsersRound className="mb-4 h-12 w-12 text-muted-foreground/50" />
            <h3 className="mb-2 text-lg font-medium">Team not found</h3>
            <p className="text-sm text-muted-foreground">
              The team you're looking for doesn't exist or has been removed.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Get worker and manager details from junction table data
  const workers = teamMembers?.workerIds
    .map(id => driversMap.get(id))
    .filter((d): d is Driver => d !== undefined) ?? [];
  const managers = teamMembers?.managerIds
    .map(id => driversMap.get(id))
    .filter((d): d is Driver => d !== undefined) ?? [];

  return (
    <div className="space-y-6">
      {/* Header with back link */}
      <div className="flex items-center justify-between">
        <Link to="/teams" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Teams
        </Link>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setIsEditModalOpen(true)}>
            <Edit className="mr-2 h-4 w-4" />
            Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" disabled={isDeleting}>
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Team</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete "{team.name}"? This action cannot be undone.
                  Workers assigned to this team will be unassigned.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteTeam}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Team'
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Team Profile Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-6">
            {/* Icon */}
            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-blue-100">
              <UsersRound className="h-12 w-12 text-blue-600" />
            </div>

            {/* Info */}
            <div className="flex-1 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-2xl font-bold">{team.name}</h1>
                  <p className="text-muted-foreground">
                    Created {new Date(team.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  {team.enableSelfAssignment ? (
                    <Badge variant="default" className="bg-green-100 text-green-700">
                      <CheckCircle className="mr-1 h-3 w-3" />
                      Self-Assignment Enabled
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-muted-foreground">
                      <AlertCircle className="mr-1 h-3 w-3" />
                      Self-Assignment Disabled
                    </Badge>
                  )}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {/* Hub */}
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  {hub ? (
                    <Link to={`/hubs`} className="hover:underline">
                      {hub.name}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">No hub assigned</span>
                  )}
                </div>

                {/* Workers */}
                <div className="flex items-center gap-2 text-sm">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span>{workers.length} workers</span>
                </div>

                {/* Managers */}
                <div className="flex items-center gap-2 text-sm">
                  <UsersRound className="h-4 w-4 text-muted-foreground" />
                  <span>{managers.length} managers</span>
                </div>

                {/* Tasks */}
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>{team.taskCount} active tasks</span>
                </div>
              </div>
            </div>
          </div>

          {/* Auto-Dispatch Action */}
          {hub && (
            <div className="mt-6 pt-6 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Auto-Dispatch</h3>
                  <p className="text-sm text-muted-foreground">
                    Automatically assign unassigned tasks to available workers based on optimization
                  </p>
                </div>
                <Button onClick={handleAutoDispatch} disabled={isDispatching}>
                  {isDispatching ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Dispatching...
                    </>
                  ) : (
                    <>
                      <Zap className="mr-2 h-4 w-4" />
                      Trigger Auto-Dispatch
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Workers List */}
      <Card>
        <CardHeader>
          <CardTitle>Team Workers ({workers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {workers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Users className="mb-4 h-12 w-12 text-muted-foreground/50" />
              <h3 className="mb-2 text-lg font-medium">No workers assigned</h3>
              <p className="text-sm text-muted-foreground">
                Workers can be assigned to this team from the Onfleet dashboard.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>On Duty</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {workers?.map((worker) => worker && (
                  <TableRow
                    key={worker.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/drivers/${worker.id}`)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {worker.photoUrl ? (
                          <img
                            src={worker.photoUrl}
                            alt={worker.name}
                            className="h-8 w-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                            <Users className="h-4 w-4 text-muted-foreground" />
                          </div>
                        )}
                        <span className="font-medium">{worker.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{worker.phone}</TableCell>
                    <TableCell>
                      <Badge variant={worker.status === 'active' ? 'default' : 'secondary'}>
                        {worker.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={worker.onDuty ? 'default' : 'outline'}>
                        {worker.onDuty ? 'Yes' : 'No'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Managers List */}
      {managers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Team Managers ({managers.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {managers?.map((manager) => manager && (
                  <TableRow key={manager.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {manager.photoUrl ? (
                          <img
                            src={manager.photoUrl}
                            alt={manager.name}
                            className="h-8 w-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                            <Users className="h-4 w-4 text-muted-foreground" />
                          </div>
                        )}
                        <span className="font-medium">{manager.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{manager.phone}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Edit Team Modal */}
      <TeamFormModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        onSubmit={handleUpdateTeam}
        isProcessing={isUpdating}
        hubs={hubs}
        drivers={Array.isArray(drivers) ? drivers : []}
        team={team}
        teamMembers={teamMembers}
      />
    </div>
  );
}
